/**
 * Персистенция персональных UI-настроек пользователя (фильтры, сортировки,
 * поиск и настройки таблицы представлений).
 *
 * Раньше эти настройки жили только в localStorage браузера. Теперь они
 * персистятся в БД отдельно для каждого пользователя через
 * `/api/preferences`, а localStorage остаётся локальным кэшем — он даёт
 * мгновенный синхронный рендер (хуки читают его в render-фазе), пока с
 * сервера не приедет актуальное состояние.
 *
 * Контракт ключей: на сервере ключ хранится без префикса `lens:`
 * (например `table:{spaceId}:{viewId}`), в localStorage — с префиксом
 * (`lens:table:...`). Управляемые префиксы перечислены в MANAGED_PREFIXES.
 */
import type { PreferencesMapResponse } from '@lens/shared';
import { apiRequest } from '../api/client.ts';

const LS_PREFIX = 'lens:';

// Префиксы ключей, которыми управляет этот стор (и которые чистит сброс).
// Указаны БЕЗ `lens:` — это серверный формат ключа.
const MANAGED_PREFIXES = ['table:', 'viewstate:', 'ui:'];

// Кому принадлежит localStorage-кэш. Ключи кэша не включают userId, поэтому
// на общей машине без этой метки настройки пользователя A утекали бы
// пользователю B (и миграция заливала бы их в аккаунт B на сервер).
const OWNER_LS_KEY = 'lens:meta:userId';

// ─── глобальные UI-преференсы ────────────────────────────────────────────────

// Ключ исторически называется `tableFontSize`, но теперь управляет размером
// шрифта всего интерфейса (не только таблиц) — ключ не переименовываем, чтобы
// сохранить уже накопленные значения пользователей на сервере и в localStorage.
const FONT_SIZE_KEY = 'ui:tableFontSize';
export const DEFAULT_TABLE_FONT_SIZE = 12;
export const TABLE_FONT_SIZE_OPTIONS = [11, 12, 13, 14, 16] as const;

// Фиксированный эталон масштаба: при этом размере множитель = 1, т.е. AntD
// остаётся на своём базовом 14px (исходный вид интерфейса). НЕ равен
// DEFAULT_TABLE_FONT_SIZE намеренно — дефолт можно менять, не сдвигая эталон,
// иначе смена дефолта не меняла бы фактический размер.
export const FONT_SCALE_BASELINE = 13;

// Подписчики на смену размера шрифта. Нужны, чтобы ThemeProvider пересобрал
// AntD ThemeConfig (token.fontSize) — CSS-переменной одной недостаточно, т.к.
// AntD задаёт размеры компонентам в px через свои токены, а не наследует от body.
const fontListeners = new Set<() => void>();

function notifyFontListeners(): void {
  for (const cb of fontListeners) cb();
}

/** Подписаться на изменение размера шрифта интерфейса. Возвращает отписку. */
export function subscribeUiFontSize(cb: () => void): () => void {
  fontListeners.add(cb);
  return () => {
    fontListeners.delete(cb);
  };
}

/** Прочитать значение преференса из localStorage-кэша (синхронно). */
export function readPref<T>(serverKey: string): T | undefined {
  try {
    const raw = localStorage.getItem(lsKey(serverKey));
    return raw == null ? undefined : (JSON.parse(raw) as T);
  } catch {
    return undefined;
  }
}

/** Текущий размер шрифта интерфейса (из кэша) или дефолт. */
export function getTableFontSize(): number {
  const v = readPref<number>(FONT_SIZE_KEY);
  return typeof v === 'number' && Number.isFinite(v) ? v : DEFAULT_TABLE_FONT_SIZE;
}

/**
 * Применить размер шрифта к :root через CSS-переменные.
 * `--lens-table-font-size` — абсолютный px для грида представлений (обратная
 * совместимость), `--lens-font-scale` — множитель относительно дефолта (13px),
 * которым масштабируется базовый текст вне AntD и AntD `token.fontSize`.
 * При дефолтном размере scale = 1, т.е. текущий вид интерфейса не меняется.
 */
export function applyTableFontSize(size: number = getTableFontSize()): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement.style;
  root.setProperty('--lens-table-font-size', `${size}px`);
  root.setProperty('--lens-font-scale', String(size / FONT_SCALE_BASELINE));
}

/** Сохранить размер шрифта (кэш + сервер), применить немедленно и оповестить подписчиков. */
export function setTableFontSize(size: number): void {
  writePref(FONT_SIZE_KEY, size);
  applyTableFontSize(size);
  notifyFontListeners();
}

// ─── недавние пространства ────────────────────────────────────────────────────

const RECENT_SPACES_KEY = 'ui:recentSpaces';
const RECENT_SPACES_LIMIT = 6;

/** Минимальная запись недавнего пространства (человекочитаемая, не сырой id). */
export interface RecentSpace {
  id: string;
  slug: string;
  name: string;
  icon?: string | null;
  ts: number;
}

function isRecentSpace(v: unknown): v is RecentSpace {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Record<string, unknown>;
  return (
    typeof r.id === 'string' &&
    typeof r.slug === 'string' &&
    typeof r.name === 'string' &&
    typeof r.ts === 'number'
  );
}

/**
 * Прочитать список недавно открытых пространств (синхронно, из кэша).
 * Битый/чужой формат деградирует в пустой массив — страница не падает.
 * Это вспомогательный блок поверх listSpaces, а не единственный источник истины.
 */
export function getRecentSpaces(): RecentSpace[] {
  const raw = readPref<unknown>(RECENT_SPACES_KEY);
  if (!Array.isArray(raw)) return [];
  return raw.filter(isRecentSpace).slice(0, RECENT_SPACES_LIMIT);
}

/**
 * Добавить пространство в начало списка недавних (дедуп по id, ограничение длины).
 * Вызывать при заходе в пространство.
 */
export function pushRecentSpace(
  space: Pick<RecentSpace, 'id' | 'slug' | 'name'> & { icon?: string | null },
): void {
  const entry: RecentSpace = {
    id: space.id,
    slug: space.slug,
    name: space.name,
    icon: space.icon ?? null,
    ts: Date.now(),
  };
  const next = [entry, ...getRecentSpaces().filter((s) => s.id !== entry.id)].slice(
    0,
    RECENT_SPACES_LIMIT,
  );
  writePref(RECENT_SPACES_KEY, next);
}

// ─── избранное (пространства и представления) ────────────────────────────────

const FAVORITES_KEY = 'ui:favorites';

/**
 * Запись избранного. Храним человекочитаемые поля (name/slug/icon), а не только
 * сырой id — чтобы рельс рендерил ссылку и подпись без дополнительного запроса,
 * даже до загрузки списка пространств/представлений (тот же приём, что у
 * RecentSpace). Дискриминатор `kind` различает пространство и представление.
 */
export type FavoriteEntry =
  | { kind: 'space'; id: string; slug: string; name: string; icon?: string | null }
  | {
      kind: 'view';
      id: string;
      spaceId: string;
      spaceSlug: string;
      name: string;
      viewType: 'table' | 'kanban';
      icon?: string | null;
    };

/** Стабильная идентичность записи избранного (для дедупа и dnd-ключа). */
export function favoriteKey(kind: FavoriteEntry['kind'], id: string): string {
  return `${kind}:${id}`;
}

function isFavoriteEntry(v: unknown): v is FavoriteEntry {
  if (typeof v !== 'object' || v === null) return false;
  const r = v as Record<string, unknown>;
  if (typeof r.id !== 'string' || typeof r.name !== 'string') return false;
  if (r.kind === 'space') return typeof r.slug === 'string';
  if (r.kind === 'view') {
    return (
      typeof r.spaceId === 'string' &&
      typeof r.spaceSlug === 'string' &&
      (r.viewType === 'table' || r.viewType === 'kanban')
    );
  }
  return false;
}

// Подписчики на изменение избранного — рельс перерисовывается, когда звёздочку
// жмут из любого места (строка пространства, ссылка представления, сама секция).
const favListeners = new Set<() => void>();

function notifyFavListeners(): void {
  for (const cb of favListeners) cb();
}

/** Подписаться на изменение избранного. Возвращает отписку. */
export function subscribeFavorites(cb: () => void): () => void {
  favListeners.add(cb);
  return () => {
    favListeners.delete(cb);
  };
}

/** Прочитать избранное (синхронно из кэша). Битый/чужой формат → пустой список. */
export function getFavorites(): FavoriteEntry[] {
  const raw = readPref<unknown>(FAVORITES_KEY);
  if (!Array.isArray(raw)) return [];
  // Дедуп по (kind,id) на случай рассинхронизации между вкладками.
  const seen = new Set<string>();
  const out: FavoriteEntry[] = [];
  for (const v of raw) {
    if (!isFavoriteEntry(v)) continue;
    const k = favoriteKey(v.kind, v.id);
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(v);
  }
  return out;
}

/** Есть ли цель в избранном. */
export function isFavorited(kind: FavoriteEntry['kind'], id: string): boolean {
  const k = favoriteKey(kind, id);
  return getFavorites().some((f) => favoriteKey(f.kind, f.id) === k);
}

function writeFavorites(next: FavoriteEntry[]): void {
  writePref(FAVORITES_KEY, next);
  notifyFavListeners();
}

/**
 * Переключить избранность цели: добавить в конец, если её нет, иначе убрать.
 * `entry` несёт актуальные name/slug/icon — при добавлении они и сохранятся.
 */
export function toggleFavorite(entry: FavoriteEntry): void {
  const k = favoriteKey(entry.kind, entry.id);
  const current = getFavorites();
  const exists = current.some((f) => favoriteKey(f.kind, f.id) === k);
  const next = exists
    ? current.filter((f) => favoriteKey(f.kind, f.id) !== k)
    : [...current, entry];
  writeFavorites(next);
}

/** Убрать цель из избранного (idempotent). */
export function removeFavorite(kind: FavoriteEntry['kind'], id: string): void {
  const k = favoriteKey(kind, id);
  const next = getFavorites().filter((f) => favoriteKey(f.kind, f.id) !== k);
  writeFavorites(next);
}

/** Задать новый порядок избранного (после drag-and-drop). */
export function setFavoritesOrder(next: FavoriteEntry[]): void {
  writeFavorites(next);
}

// ─── заметки о релизах («Что нового») ────────────────────────────────────────

const RELEASES_SEEN_KEY = 'ui:releasesSeen';

// Подписчики на изменение «прочитано» — кнопка в рельсе гасит подсветку сразу
// после открытия страницы релизов и зажигает её после гидрации с сервера.
const releasesListeners = new Set<() => void>();

function notifyReleasesListeners(): void {
  for (const cb of releasesListeners) cb();
}

/** Подписаться на изменение отметки о прочитанных релизах. Возвращает отписку. */
export function subscribeReleasesSeen(cb: () => void): () => void {
  releasesListeners.add(cb);
  return () => {
    releasesListeners.delete(cb);
  };
}

/**
 * Идентификатор самого свежего релиза, который пользователь уже видел.
 * `null` — не видел ни одного (новичок или настройка ещё не приехала с сервера).
 */
export function getReleasesSeen(): string | null {
  const v = readPref<unknown>(RELEASES_SEEN_KEY);
  return typeof v === 'string' && v.length > 0 ? v : null;
}

/** Отметить релиз прочитанным (кэш + сервер) и оповестить подписчиков. */
export function setReleasesSeen(releaseId: string): void {
  if (getReleasesSeen() === releaseId) return;
  writePref(RELEASES_SEEN_KEY, releaseId);
  notifyReleasesListeners();
}

function isManagedKey(serverKey: string): boolean {
  return MANAGED_PREFIXES.some((p) => serverKey.startsWith(p));
}

function lsKey(serverKey: string): string {
  return LS_PREFIX + serverKey;
}

// ─── debounced server writes (по ключу) ──────────────────────────────────────

const pendingTimers = new Map<string, ReturnType<typeof setTimeout>>();
const PUT_DEBOUNCE_MS = 600;

async function putPreference(serverKey: string, value: unknown): Promise<void> {
  try {
    await apiRequest<void>(`/api/preferences/${encodeURIComponent(serverKey)}`, {
      method: 'PUT',
      body: { value },
    });
  } catch (err) {
    // Сетевые сбои не должны ломать UX — localStorage уже обновлён,
    // настройка не теряется, синхронизация догонит на следующей записи.
    console.warn('prefs: PUT failed', serverKey, err);
  }
}

/**
 * Записать настройку: синхронно в localStorage-кэш + debounced PUT на сервер.
 * `serverKey` — без префикса `lens:` (например `table:{spaceId}:{viewId}`).
 */
export function writePref(serverKey: string, value: unknown): void {
  try {
    localStorage.setItem(lsKey(serverKey), JSON.stringify(value));
  } catch {
    /* quota exceeded — игнорируем, сервер всё равно получит значение */
  }
  const existing = pendingTimers.get(serverKey);
  if (existing) clearTimeout(existing);
  pendingTimers.set(
    serverKey,
    setTimeout(() => {
      pendingTimers.delete(serverKey);
      void putPreference(serverKey, value);
    }, PUT_DEBOUNCE_MS),
  );
}

// ─── hydration ────────────────────────────────────────────────────────────────

/** Вычистить все управляемые `lens:*`-ключи из localStorage-кэша. */
export function clearPreferenceCache(): void {
  try {
    const toRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const fullKey = localStorage.key(i);
      if (!fullKey || !fullKey.startsWith(LS_PREFIX)) continue;
      if (isManagedKey(fullKey.slice(LS_PREFIX.length))) toRemove.push(fullKey);
    }
    for (const k of toRemove) localStorage.removeItem(k);
    localStorage.removeItem(OWNER_LS_KEY);
  } catch {
    /* localStorage недоступен — игнорируем */
  }
}

/**
 * Подтянуть настройки пользователя с сервера в localStorage-кэш. Вызывается
 * после успешной аутентификации, ДО монтирования страниц, читающих кэш.
 *
 * Дополнительно выполняет одноразовую миграцию: настройки, которые есть в
 * localStorage этого браузера, но отсутствуют на сервере (пользователь
 * настраивал представления до перехода на серверное хранение), заливаются
 * на сервер.
 *
 * `currentUserId` — владелец сессии. Если кэш принадлежал другому
 * пользователю (общий браузер), он очищается, а миграция local-only ключей
 * пропускается — иначе настройки предыдущего пользователя утекли бы в
 * аккаунт текущего.
 */
export async function hydratePreferences(currentUserId: string): Promise<void> {
  let ownerChanged = false;
  try {
    const cachedOwner = localStorage.getItem(OWNER_LS_KEY);
    ownerChanged = cachedOwner != null && cachedOwner !== currentUserId;
    if (ownerChanged) clearPreferenceCache();
  } catch {
    /* localStorage недоступен */
  }

  let serverPrefs: Record<string, unknown> = {};
  try {
    const res = await apiRequest<PreferencesMapResponse>('/api/preferences', {
      method: 'GET',
    });
    serverPrefs = res.preferences ?? {};
  } catch (err) {
    // Без серверных настроек просто работаем на localStorage — не блокируем вход.
    console.warn('prefs: hydrate failed', err);
    return;
  }

  // Кэш с этого момента принадлежит текущему пользователю.
  try {
    localStorage.setItem(OWNER_LS_KEY, currentUserId);
  } catch {
    /* quota */
  }

  // Серверное состояние — источник правды: пишем его в кэш.
  for (const [serverKey, value] of Object.entries(serverPrefs)) {
    try {
      localStorage.setItem(lsKey(serverKey), JSON.stringify(value));
    } catch {
      /* quota */
    }
  }

  // Применяем глобальные UI-преференсы сразу после гидрации и оповещаем
  // подписчиков (ThemeProvider), чтобы AntD-тема подхватила серверное значение.
  applyTableFontSize();
  notifyFontListeners();
  // Избранное приехало с сервера прямо в кэш (минуя writePref) — оповещаем
  // рельс, иначе секция «Избранное» осталась бы пустой до первого тоггла.
  notifyFavListeners();
  // То же для отметки о прочитанных релизах: без оповещения кнопка «Что нового»
  // в новом браузере светилась бы непрочитанным, хотя на сервере отметка есть.
  notifyReleasesListeners();

  // Миграция local-only настроек на сервер (одноразово при первом входе
  // после деплоя). Пушим всё, чего ещё нет на сервере. При смене
  // пользователя миграцию пропускаем — кэш уже очищен, а остаточные ключи
  // не должны попасть в чужой аккаунт.
  if (ownerChanged) return;
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const fullKey = localStorage.key(i);
      if (!fullKey || !fullKey.startsWith(LS_PREFIX)) continue;
      const serverKey = fullKey.slice(LS_PREFIX.length);
      if (!isManagedKey(serverKey)) continue;
      if (serverKey in serverPrefs) continue;
      const raw = localStorage.getItem(fullKey);
      if (raw == null) continue;
      try {
        void putPreference(serverKey, JSON.parse(raw));
      } catch {
        /* битый JSON — пропускаем */
      }
    }
  } catch {
    /* доступ к localStorage недоступен — игнорируем */
  }
}

// ─── reset ────────────────────────────────────────────────────────────────────

/**
 * Сбросить все персональные настройки: удалить на сервере и вычистить
 * localStorage-кэш. После вызова страницу следует перезагрузить, чтобы
 * хуки переинициализировались с дефолтами.
 */
export async function resetAllPreferences(): Promise<void> {
  await apiRequest<void>('/api/preferences', { method: 'DELETE' });

  // Чистим управляемые ключи из localStorage.
  clearPreferenceCache();
}
